import React, { useContext, useState } from "react";
import { useForm } from "react-hook-form";
import {
    Form,
    FormFields,
    FormActions,
    FormDropdown,
    mapToDropdownOption,
    ValidationResult,
    FormValidationSummary,
    useNotifications
} from "sprint-common-web";

import { LanguagesDataContext } from "../../../../../../../../common/data/lookup-data-providers/LanguagesDataProvider";
import { useCloudAssetDetails } from "../../../../../common/hooks/useCloudAssetDetails";
import { useEditCloudSoundtrackAssetLanguage } from "./useEditCloudSoundtrackAssetLanguage";

export interface CloudAudioAssetLanguageFormProps {
    onDismiss(): void;
    onSubmit(newAssetId: string): void;
    soundtrackId: string;
}

interface CloudAudioAssetLanguageFormData {
    soundtrackId: string;
}

export const CloudAudioAssetLanguageForm: React.FC<CloudAudioAssetLanguageFormProps> = ({
    onDismiss,
    onSubmit,
    soundtrackId
}: CloudAudioAssetLanguageFormProps) => {
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [validationResult, setValidationResult] = useState<ValidationResult>();
    const { dubbingLanguages } = useContext(LanguagesDataContext);
    const { data: assetDetails } = useCloudAssetDetails();
    const { success, error } = useNotifications();
    const {
        validateCloudSoundtrackAssetLanguage,
        updateCloudSoundtrackAssetLanguage
    } = useEditCloudSoundtrackAssetLanguage({
        invalidationKey: assetDetails?.assetId
    });

    const { handleSubmit, errors, control, formState } = useForm<CloudAudioAssetLanguageFormData>({
        defaultValues: {
            soundtrackId
        }
    });

    const handleSubtitleUpdate = async ({ soundtrackId: soundtrackLanguageId }: CloudAudioAssetLanguageFormData) => {
        setIsLoading(true);

        try {
            const result = await validateAndSetResult(soundtrackLanguageId);

            if (!result?.isValid) {
                setIsLoading(false);
                return;
            }

            if (!assetDetails) {
                return;
            }

            const updateResponse = await updateCloudSoundtrackAssetLanguage.mutateAsync({
                assetId: assetDetails.assetId,
                soundtrackLanguageId
            });

            onSubmit(updateResponse.newAssetId);

            success("Soundtrack language updated successfully.");
        } catch (e) {
            error("Failed to update soundtrack language.");
        } finally {
            setIsLoading(false);
        }
    };

    const validateAndSetResult = async (soundtrackLanguageId: string) => {
        if (!assetDetails) {
            return;
        }

        const result = await validateCloudSoundtrackAssetLanguage.mutateAsync({
            assetId: assetDetails.assetId,
            soundtrackLanguageId
        });

        setValidationResult(result);

        return result;
    };

    return (
        <Form onSubmit={handleSubmit(handleSubtitleUpdate)}>
            <FormFields>
                <FormDropdown
                    options={dubbingLanguages.map((dub) => mapToDropdownOption(dub))}
                    label="Soundtrack"
                    name="soundtrackId"
                    control={control}
                    errors={errors}
                    disabled={!dubbingLanguages?.length}
                    disableClear={true}
                />
                {validationResult && <FormValidationSummary validationResult={validationResult} />}
                <FormActions
                    onCancel={onDismiss}
                    isLoading={formState.isValidating || formState.isSubmitting || isLoading}
                    submitDisabled={!formState.isDirty}
                />
            </FormFields>
        </Form>
    );
};
